import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, CurrencyDollar, Users, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, saveAuth } from "@/lib/api";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";
import ReferrerPicker from "@/components/ReferrerPicker";
import { STEP0_FLAG } from "@/components/TrialSurfaces";
import {
  getRefSig, setRefSig as storeRefSig, clearRefSig,
  getFromSilo, setFromSilo as storeFromSilo, clearFromSilo,
  getVisitorSig,
} from "@/lib/attribution";

/* ─────────────────────────────────────────────────────────────────────────────
   Oct 2026 (Randy pt-sunrise-signup) — SUNRISE THEME, ONE SWITCH.

   Same deal as Landing.jsx: the form's STRUCTURE, copy, validation and every
   data-testid are untouched. Only the class strings move, and they move
   through this one table. Flip SUNRISE_ON to false and the original cream
   page returns exactly as it was; `?classic=1` does the same live with no
   deploy, so the two can be compared in two tabs.

   Palette is sampled from this page's own photograph: sun core #FFC24A, warm
   amber #E07B2C, deep sky #0B1320, warm off-white #F5F1E8 for type.
   ──────────────────────────────────────────────────────────────────────────── */
const SUNRISE_ON = true;

// 24x16 blur of the hero photo, so the page paints warm instead of black
// while the real image streams in. ~500 bytes and no extra request.
const LQIP = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABQODxIPDRQSEBIXFRQYHjIhHhwcHj0sLiQySUBMS0dARkVQWnNiUFVtVkVGZIhlbXd7gYKBTmCNl4x9lnN+gXz/2wBDARUXFx4aHjshITt8U0ZTfHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHz/wAARCAAQABgDASIAAhEBAxEB/8QAFwAAAwEAAAAAAAAAAAAAAAAAAAQGBf/EACEQAAICAQMFAQAAAAAAAAAAAAECAAMEBRExEhMhUYFh/8QAFQEBAQAAAAAAAAAAAAAAAAAAAgP/xAAbEQADAAIDAAAAAAAAAAAAAAAAAQIDIRIxQf/aAAwDAQACEQMRAD8AVpWkEbuo+iaWNTQxALLJNLO3sT5+xunURUwZQCf2G6prTHKldoqb6sallBdAzcD3CSObqFt+QlwIHTwBxCSU5Gt0U5QvD//Z";

const PAPER = {
  bg: false,
  page: "min-h-screen bg-[#FDFCF8] flex flex-col",
  nav: "border-b hairline",
  logoDot: "w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center",
  signIn: "text-sm text-[#6B6A68] hover:text-[#1C1B1A]",
  card: "w-full max-w-md bg-white border hairline rounded-xl shadow-sm p-8",
  h1: "font-serif-display text-3xl text-[#1C1B1A]",
  sub: "text-[#6B6A68] text-sm mt-1",
  panel: "mt-5 bg-[#F0F1EE] border border-[#4A5D4E]/20 rounded-lg p-4",
  panelAlt: "mt-5 bg-[#FDFCF8] border hairline rounded-lg p-4",
  eyebrow: "flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A5D4E] mb-2",
  strong: "text-[#1C1B1A]",
  muted: "text-[#6B6A68]",
  fine: "text-[#6B6A68]",
  accentIcon: "text-[#4A5D4E]",
  label: "block text-sm text-[#6B6A68]",
  input: "w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]",
  tosLink: "underline hover:text-[#4A5D4E]",
  checkbox: "mt-1",
  pickerPrimary: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1C1B1A] text-[#FDFCF8] text-[11px] font-medium hover:bg-[#3A4A3E] transition-colors",
  pickerGhost: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hairline bg-white text-[#6B6A68] text-[11px] hover:text-[#1C1B1A] hover:border-[#1C1B1A] transition-colors",
  pickerOption: "w-full flex items-center justify-between gap-3 text-left px-3 py-2 rounded-md border hairline bg-white text-[#1C1B1A] text-xs hover:border-[#4A5D4E] transition-colors",
  submit: "mt-6 w-full bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] px-6 py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60",
};

const SUNRISE = {
  bg: true,
  // `relative isolate` is load-bearing: it makes this div a stacking context
  // so the backdrop's negative z-index is CONTAINED here. Without it the
  // layer paints behind `#root { background: #FDFCF8 }` and the photo never
  // appears. Which is also why this variant carries no background colour of
  // its own — the backdrop supplies the base.
  page: "min-h-screen flex flex-col text-[#F5F1E8] relative isolate",
  nav: "border-b border-[#F5F1E8]/10 bg-[#070B11]/70 backdrop-blur-md",
  logoDot: "w-9 h-9 rounded-full bg-gradient-to-br from-[#FFC24A] to-[#E07B2C] text-[#1A1206] flex items-center justify-center shadow-[0_0_24px_rgba(255,194,74,0.35)]",
  signIn: "text-sm text-[#E7E2D6]/75 hover:text-[#FFC24A] transition-colors",
  // `backdrop-brightness` is what makes the glass self-correcting. A flat
  // tint fails on this photo: the value that reads right over the forest
  // leaves white type invisible over the sun. Multiplying what sits behind
  // the glass darkens the blown-out sun and the dark forest by the same
  // proportion, so the type is legible wherever the card lands.
  card: "w-full max-w-md rounded-2xl p-8 border border-[#F5F1E8]/14 bg-[#060A12]/20 backdrop-blur-2xl backdrop-brightness-[0.44] backdrop-saturate-150 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.75)]",
  h1: "font-serif-display text-3xl text-[#F5F1E8]",
  sub: "text-[#E7E2D6]/75 text-sm mt-1",
  panel: "mt-5 rounded-lg p-4 border border-[#FFC24A]/25 bg-[#FFC24A]/[0.07]",
  panelAlt: "mt-5 rounded-lg p-4 border border-[#F5F1E8]/14 bg-[#F5F1E8]/[0.05]",
  eyebrow: "flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FFC24A] mb-2",
  strong: "text-[#F5F1E8]",
  muted: "text-[#E7E2D6]/75",
  fine: "text-[#E7E2D6]/60",
  accentIcon: "text-[#FFC24A]",
  label: "block text-sm text-[#E7E2D6]/80",
  input: "w-full rounded-md px-4 py-3 bg-[#F5F1E8]/[0.07] border border-[#F5F1E8]/20 text-[#F5F1E8] placeholder:text-[#F5F1E8]/35 backdrop-blur-sm focus:outline-none focus:ring-1 focus:ring-[#FFC24A] focus:border-[#FFC24A] transition-colors",
  tosLink: "underline hover:text-[#FFC24A]",
  checkbox: "mt-1 accent-[#FFC24A]",
  pickerPrimary: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5EFE2] text-[#14110C] text-[11px] font-medium hover:bg-[#FFC24A] transition-colors",
  pickerGhost: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#F5F1E8]/22 text-[#E7E2D6]/85 text-[11px] hover:border-[#FFC24A]/50 hover:text-[#FFC24A] transition-colors",
  pickerOption: "w-full flex items-center justify-between gap-3 text-left px-3 py-2 rounded-md border border-[#F5F1E8]/14 bg-[#F5F1E8]/[0.04] text-[#F5F1E8] text-xs hover:border-[#FFC24A]/45 hover:bg-[#FFC24A]/[0.07] transition-colors",
  submit: "mt-6 w-full bg-[#F5EFE2] text-[#14110C] hover:bg-[#FFC24A] px-6 py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_40px_-8px_rgba(255,194,74,0.5)]",
};

// Fixed photographic layer. A real fixed <img> rather than
// `background-attachment: fixed`, which iOS Safari has never supported
// properly and which would judder the whole page on his phone.
const SunriseBackdrop = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden" aria-hidden="true" data-testid="signup-sunrise-backdrop">
    <div className="absolute inset-0 bg-[#0A0D0F]" />
    <div className="absolute inset-0 bg-cover bg-center scale-105 blur-xl" style={{ backgroundImage: `url(${LQIP})` }} />
    <img
      src="/brand/signup-sunrise-1536.jpg"
      srcSet="/brand/signup-sunrise-960.webp 960w, /brand/signup-sunrise-1536.webp 1536w"
      sizes="100vw"
      alt=""
      fetchpriority="high"
      decoding="async"
      className="absolute inset-0 w-full h-full object-cover object-[50%_42%] brightness-[0.84] saturate-[1.06]"
    />
    {/* Readability scrim. Near-transparent through the middle on purpose so
        the sun still reads as a sunrise instead of a grey wash: the form's own
        frosted panel is what guarantees contrast for the type. */}
    <div className="absolute inset-0 bg-gradient-to-b from-[#04070E]/58 via-[#050810]/14 to-[#07050A]/70" />
    {/* Warm bloom lifted off the sun, so the amber accents have something to echo. */}
    <div className="absolute right-[6%] top-[18%] w-[50vw] h-[50vw] rounded-full bg-[#FFB347]/10 blur-[120px]" />
  </div>
);

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", tos: false });
  const [loading, setLoading] = useState(false);
  const [refSig, setRefSig] = useState("");
  const [fromSilo, setFromSilo] = useState("");
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  // Oct 2026 — `?classic=1` forces the original cream page. Two tabs, same
  // build, instant A/B, and a rollback that needs no deploy.
  const classic = searchParams.get("classic") === "1";
  const T = (SUNRISE_ON && !classic) ? SUNRISE : PAPER;
  const _pricing = usePublicPricing();
  const trialPrice = fmt$(_pricing.creator_monthly_fee_cents ?? _pricing.platform_fee_cents);

  // Oct 2026 (Randy, fan-first pass) — which doorway brought them here.
  // "fan" joins free and lands in the Fan Portal; "creator" and "trial" go to
  // /start, where the two ways to begin are laid out. Anything else (a bare
  // /signup, an old bookmark) behaves exactly as it always did.
  const intentParam = (searchParams.get("as") || "").toLowerCase();
  const intent = ["fan", "creator", "trial"].includes(intentParam) ? intentParam : null;
  const isFan = intent === "fan";
  // Nov 2026 (Randy) — arrived from the "invite them" button on the marketing
  // page or a fan portal, so the top of the card tells the referral story.
  const viaShare = (searchParams.get("via") || "").toLowerCase() === "share";

  // Nov 2026 — who sent them, by NAME. Resolved from the ref code so the card
  // can say it out loud instead of asking. Name and picture only; an email
  // address is never exposed by that endpoint.
  const [inviter, setInviter] = useState(null);

  // iter-31 + P0 (Feb 7, 2026): pull ?ref= from URL (fresh click) or
  // localStorage (carried over from any prior PublicSilo / Landing visit
  // — including across tabs and browser restarts). URL wins.
  // iter-32: also read `mysilo_from_silo` — silo-channel attribution
  // fallback when no explicit ?ref= exists.
  useEffect(() => {
    const urlRef = searchParams.get("ref");
    if (urlRef) {
      storeRefSig(urlRef);
      setRefSig(urlRef);
    } else {
      const saved = getRefSig();
      if (saved) setRefSig(saved);
    }
    const urlFromSilo = searchParams.get("from_silo");
    if (urlFromSilo) {
      storeFromSilo(urlFromSilo);
      setFromSilo(urlFromSilo);
    } else {
      const saved = getFromSilo();
      if (saved) setFromSilo(saved);
    }
  }, [searchParams]);

  // Resolve the remembered silo to its real identity for the fan banner.
  // Light public endpoint — name + tagline only, no side effects.
  const [siloCard, setSiloCard] = useState(null);
  useEffect(() => {
    if (!fromSilo || refSig) { setSiloCard(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/s/${encodeURIComponent(fromSilo)}/card`);
        if (!cancelled) setSiloCard(r.data);
      } catch { if (!cancelled) setSiloCard(null); }
    })();
    return () => { cancelled = true; };
  }, [fromSilo, refSig]);

  // Nov 2026 (Randy) — resolve the ref code to a NAME so the card can say who
  // invited them. A code that resolves to nobody falls back to the picker, so a
  // stale or mistyped link never silently claims a stranger sent them.
  useEffect(() => {
    if (!refSig) { setInviter(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/public/referrer/${encodeURIComponent(refSig)}`);
        if (!cancelled) setInviter(r.data?.name ? r.data : null);
      } catch { if (!cancelled) setInviter(null); }
    })();
    return () => { cancelled = true; };
  }, [refSig]);

  // Oct 2026 (Randy, seeding pass) — the user's own answer to "who sent you?".
  // Null until they touch it: an untouched picker means "no explicit answer",
  // and the backend keeps its existing guess chain. Any answer is a HARD
  // signal that outranks every guess and freezes attribution.
  const [referrerChoice, setReferrerChoice] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tos) return toast.error("You must accept the Terms of Service to continue.");
    setLoading(true);
    try {
      // iter-35e: ALWAYS send from_silo_slug if available.
      // P0 Feb 7 — also forward visitor_sig cookie. When localStorage was
      // wiped (private mode, manual clear, fresh browser) but the cookie
      // survived (400-day expiry), the backend uses it as a final fallback
      // to look up any pending referral row keyed to this visitor and
      // attach the signup to its inviter.
      const r = await api.post("/auth/signup", {
        name: form.name, email: form.email, password: form.password, tos_accepted: true,
        referrer_sig: refSig || undefined,
        from_silo_slug: fromSilo || undefined,
        visitor_sig: getVisitorSig() || undefined,
        signup_intent: intent || undefined,
        referrer_choice_mode: referrerChoice?.mode || undefined,
        referrer_choice_slug: referrerChoice?.slug || undefined,
      });
      saveAuth(r.data.token, r.data.user);
      clearRefSig();
      clearFromSilo();
      // Oct 2026 (TRIAL_PLAN.md §8.3) — Step 0 arms the trial offer on the
      // first authed screen. Oct 2026 (fan-first pass): ONLY for someone who
      // came through the trial door. A fan who joined free is not greeted by a
      // pitch for a paid tier, and a creator gets the real choice on /start
      // instead of a popup over the top of it.
      if (intent === "trial") {
        try { sessionStorage.setItem(STEP0_FLAG, "1"); } catch { /* storage off */ }
      }
      toast.success(`Welcome, ${r.data.user.name}.`);
      nav(isFan ? "/fan-portal" : intent ? "/start" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className={T.page} data-testid="signup-page">
      {T.bg && <SunriseBackdrop />}
      <nav className={T.nav}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className={T.logoDot}><Sparkle size={18} weight="duotone" /></div>
            <span className="font-serif-display text-xl">My Silo</span>
          </Link>
          <Link to="/login" className={T.signIn}>Already have an account? <span className="underline">Sign in</span></Link>
        </div>
      </nav>

      <main className="siloart-copy flex-1 flex items-center justify-center px-6 py-12">
        <motion.form
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className={T.card}
        >
          <h1 className={T.h1}>{isFan ? "Join My Silo." : "Create your silo."}</h1>
          {!isFan && <p className={T.sub}>Your private AI twin starts here.</p>}

          {/* Oct 2026 (Randy, fan-first pass) — what a free fan account IS.
              Nothing on the old signup form said it, so the only story on the
              page was the paid one.
              Nov 2026 — arriving from the share button (`?via=share`) swaps
              this top block for the invite story. Earnings wording stays
              careful on purpose: a referral is tracked for every member, but
              commission is a Creator Pro programme and nothing here may read
              as a promise of income. */}
          {isFan && (
            <div className={T.panelAlt} data-testid={viaShare ? "signup-share-terms" : "signup-fan-terms"}>
              <div className={T.eyebrow}>
                <Users size={12} weight="duotone" /> {viaShare ? "Know people who could use this?" : "Free fan account"}
              </div>
              <ul className={`text-xs space-y-1.5 ${T.muted}`}>
                {viaShare ? (
                  <>
                    <li>• Every account gets its own share link — hand it to anyone, no silo of your own required.</li>
                    <li>• We track who arrives on it from the moment they land, even while you're only a fan.</li>
                    <li>• Become a Creator Pro and those tracked arrivals can start earning you referral commission.</li>
                    <li>• Sign up free and your link is waiting in your portal on the next page.</li>
                  </>
                ) : (
                  <>
                    <li>• Browse public silos — digital personas, YouTube digital twins, experts who've published their corpus.</li>
                    <li>• Ask them questions, bookmark the answers, and save any silo to your home screen.</li>
                    <li>• Some creators open theirs free; others charge a monthly fee you'd choose to pay, per silo.</li>
                    <li>• Want to build your own later? Becoming a creator is one tap from inside your portal.</li>
                  </>
                )}
              </ul>
            </div>
          )}

          {/* Oct 2026 (TRIAL_PLAN.md §8.2, Phase 9) — the trial terms stated
              BEFORE the form, not after the card.
              Oct 2026 (Randy, pass 3): shown ONLY to someone who came through
              the trial door. A visitor who clicked "Create a silo" already
              walked past the trial button on the marketing page — announcing it
              again at the top of their signup form argues them back toward a
              decision they just declined. The honesty requirement is unchanged:
              nobody reaches a card-capture screen without these terms, they're
              now read on /start's trial pill (TrialStartModal) instead, and the
              TOS checkbox below still names the conversion. */}
          {intent === "trial" && (
          <div className={T.panelAlt} data-testid="signup-trial-terms">
            <div className={T.eyebrow}>
              <Sparkle size={12} weight="duotone" /> 14 days of everything
            </div>
            <ul className={`text-xs space-y-1.5 ${T.muted}`}>
              <li>• Every Pro tool unlocked from minute one — not a feature-limited demo.</li>
              <li>• <strong className={T.strong}>{trialPrice}/month begins on day 15.</strong> A card is required to start, and we email you <strong className={T.strong}>three days before</strong> anything is charged.</li>
              <li>• Cancel in one click any time before day 15 and you are charged nothing — you keep the trial through its last day.</li>
              <li>• Pro tools you want to keep are charged separately, once, and only after you confirm each one.</li>
              <li>• AI calls are yours to fund — your own provider key, or platform credit. We start you with a small Gemini credit at cost.</li>
              <li>• Files built by the Workbench or Refinery stay in your silo but can't be opened or used without Minimum Creator Pro. Your own uploads are never locked.</li>
              <li>• Prefer to skip the trial and subscribe straight away? That choice is on the next screen.</li>
            </ul>
          </div>
          )}

          {/* iter-31: dedicated affiliate welcome banner when arriving via ?ref=
              Nov 2026 (Randy) — now NAMES the inviter when the code resolves,
              in this banner rather than a second one beneath it. Bullet three
              used to promise "ongoing monthly income" to every invited signup;
              it states the programme instead. */}
          {refSig && (
            <div className={T.panel} data-testid="signup-referral-banner">
              <div className={T.eyebrow}>
                <Sparkle size={12} weight="duotone" /> You were invited
              </div>
              {inviter ? (
                <div className={`text-sm mb-2 ${T.strong}`} data-testid="signup-inviter-named">
                  <strong>{inviter.name}</strong> sent you to My Silo, so they&apos;ll be credited as the
                  person who brought you here.
                </div>
              ) : (
                <div className={`text-sm font-medium mb-2 ${T.strong}`}>Welcome to My Silo — and welcome to the creator economy.</div>
              )}
              <ul className={`text-xs space-y-1.5 mb-3 ${T.muted}`}>
                <li className="flex items-start gap-2"><CurrencyDollar size={12} weight="duotone" className={`${T.accentIcon} mt-0.5 shrink-0`} /> <span>Build your own digital AI twin — a searchable, siloed knowledge base that answers in <em>your</em> voice from <em>your</em> content.</span></li>
                <li className="flex items-start gap-2"><Users size={12} weight="duotone" className={`${T.accentIcon} mt-0.5 shrink-0`} /> <span>Share your own link with anyone — we track who arrives on it from the moment they land, whether you&apos;re a fan or a creator.</span></li>
                <li className="flex items-start gap-2"><TrendUp size={12} weight="duotone" className={`${T.accentIcon} mt-0.5 shrink-0`} /> <span>Creator Pro members can join the Partner Program, which pays referral commission on Creator Pro software sales.</span></li>
              </ul>
              <div className={`text-[10px] italic ${T.fine}`}>
                Your referrer gets a small thank-you credit when you activate a creator subscription. The amount is platform-tunable — see your dashboard after signup for current rates.
              </div>
            </div>
          )}

          {/* Oct 2026 (Randy, seeding pass) — REPLACES the old fan-only
              silo-source banner, which was wrong in both directions: it
              promised a follow that signup deliberately does NOT create (Jun
              2026), and said nothing about the thing it DOES create — a
              referral tie to that creator worth real money for as long as the
              account lives. Now asked out loud, on every path in, with the
              answer in the user's hands.

              Nov 2026 (Randy) — EXCEPT when they arrived on somebody's link.
              *"Your link wins — don't even ask them."* An explicit `?ref=`
              already beat every guess server-side, but the picker could still
              overwrite it with a silo they merely browsed, so the question is
              no longer asked: we name the inviter instead. */}
          {inviter ? null : (
            <ReferrerPicker
              intent={intent}
              detectedSlug={fromSilo || ""}
              value={referrerChoice}
              onChange={setReferrerChoice}
              theme={T}
            />
          )}

          {/* The silo they came from, by name — kept because a fan who arrived
              through someone's public page should still see whose it was. */}
          {!refSig && fromSilo && siloCard?.name && (
            <div className={`text-[11px] mt-2 ${T.fine}`} data-testid="signup-silo-source-banner">
              You arrived from <span className={T.muted}>{siloCard.name}</span>
              {siloCard?.tagline ? ` · ${siloCard.tagline}` : ""}
            </div>
          )}

          <label className={`${T.label} mt-6 mb-1.5`}>Your name</label>
          <input data-testid="signup-name" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={T.input} placeholder="Your full name" />

          <label className={`${T.label} mt-4 mb-1.5`}>Email</label>
          <input data-testid="signup-email" type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={T.input} placeholder="you@email.com" />

          <label className={`${T.label} mt-4 mb-1.5`}>Password (min 8 chars)</label>
          <input data-testid="signup-password" type="password" required minLength={8} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={T.input} placeholder="••••••••" />

          <label className={`flex items-start gap-2 mt-5 text-sm cursor-pointer ${T.muted}`}>
            <input data-testid="signup-tos" type="checkbox" checked={form.tos} onChange={(e) => setForm({ ...form, tos: e.target.checked })} className={T.checkbox} />
            <span>
              I accept the{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className={T.tosLink}>Terms of Service</Link>,{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className={T.tosLink}>Privacy Policy</Link>, and{" "}
              <Link to="/refund-policy" target="_blank" rel="noopener noreferrer" className={T.tosLink} data-testid="signup-refund-link">Refund Policy</Link>.
              I acknowledge that I own my data and My Silo is the custodian.
              Creating an account is free — you'll only be charged if you subscribe to a creator, upgrade to Creator&nbsp;Pro, or start the 14-day Creator trial, which becomes {trialPrice}/month on day&nbsp;15 unless you cancel first. Any recurring subscription can be canceled anytime from Account&nbsp;→&nbsp;Billing.
            </span>
          </label>

          <button type="submit" disabled={loading} data-testid="signup-submit"
            className={T.submit}>
            {loading ? "Creating…" : <>{isFan ? "Join free" : "Create account"} <ArrowRight size={16} /></>}
          </button>
        </motion.form>
      </main>
    </div>
  );
}
